import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridColumnVisibilityModel, GridPaginationModel, GridRenderCellParams } from '@mui/x-data-grid';
import { DateTimePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useApplicationContext } from 'ApplicationContext';
import { AuditLogEntry } from 'apiClient/Api';
import { useDocumentTitle } from 'Hooks/useDocumentTitle';
import { useSearchParams } from 'react-router';
import { HubConnectionBuilder, HubConnectionState, LogLevel, HttpTransportType } from '@microsoft/signalr';

const AUDIT_LOG_HUB_PATH = '/hubs/audit-log';
const AUDIT_LOG_REGISTER_METHOD = 'RegisterAuditLog';
const AUDIT_LOG_UNREGISTER_METHOD = 'UnregisterAuditLog';
const AUDIT_LOG_EVENT_NAMES = ['auditlogcreated']; // SignalR .NET converts to lowercase
const DEFAULT_PAGE_SIZE = 50;

type FilterState = {
  contains: string;
  referenceType: string;
  referenceId: string;
  user: string;
  since: Dayjs | null;
  until: Dayjs | null;
};

/**
 * Reads filter state from search params.
 * @param {URLSearchParams} params Router search params.
 * @returns {FilterState} Parsed filter state.
 */
const readFiltersFromParams = (params: URLSearchParams): FilterState => {
  const since = params.get('since');
  const until = params.get('until');
  return {
    contains: params.get('contains')?.trim() ?? '',
    referenceType: params.get('referenceType')?.trim() ?? '',
    referenceId: params.get('referenceId')?.trim() ?? '',
    user: params.get('user')?.trim() ?? '',
    since: since ? dayjs(since) : null,
    until: until ? dayjs(until) : null,
  };
};

/**
 * Admin page for searching and viewing audit log entries.
 * @returns {JSX.Element} The audit log page.
 */
export const AdminAuditLog: React.FC = (): JSX.Element => {
  useDocumentTitle('base.pages.auditLog');

  const { t } = useTranslation();
  const context = useApplicationContext();
  const { api, apiError } = context;
  const [searchParams, setSearchParams] = useSearchParams();

  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [realtimeConnected, setRealtimeConnected] = useState<boolean>(false);

  const [filterInputs, setFilterInputs] = useState<FilterState>(() => readFiltersFromParams(searchParams));
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    clientIp: false,
    userAgent: false,
  });

  const activeFilters = useMemo<FilterState>(() => readFiltersFromParams(searchParams), [searchParams]);

  /**
   * Loads a page of audit entries using the active filters.
   * @returns {Promise<void>} Promise resolved when loading is complete.
   */
  const loadAuditEntries = useCallback(async (filters: FilterState, skip: number, limit: number): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.auditLog.auditLogSearchAuditEntries({
        contains: filters.contains || null,
        referenceType: filters.referenceType || null,
        referenceId: filters.referenceId || null,
        user: filters.user || null,
        since: filters.since?.isValid() ? filters.since.toISOString() : null,
        until: filters.until?.isValid() ? filters.until.toISOString() : null,
        skip,
        limit,
      });
      setEntries(response.data?.entries ?? []);
      setTotalCount(response.data?.totalCount ?? 0);
    } catch (err: any) {
      setEntries([]);
      setTotalCount(0);
      setError(err?.response?.data?.detail || t('auditLog.error'));
      apiError(err);
    } finally {
      setLoading(false);
    }
  }, [api.auditLog, apiError, t]);

  useEffect(() => {
    setFilterInputs(activeFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    loadAuditEntries(activeFilters, 0, paginationModel.pageSize);
    // We intentionally only react to URL filter changes + page size; page changes trigger their own load below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilters, paginationModel.pageSize, loadAuditEntries]);

  /**
   * Serializes current filter inputs into URL search params and triggers a reload via the effect above.
   */
  const handleApplyFilters = (): void => {
    const nextParams = new URLSearchParams();
    if (filterInputs.contains.trim()) nextParams.set('contains', filterInputs.contains.trim());
    if (filterInputs.referenceType.trim()) nextParams.set('referenceType', filterInputs.referenceType.trim());
    if (filterInputs.referenceId.trim()) nextParams.set('referenceId', filterInputs.referenceId.trim());
    if (filterInputs.user.trim()) nextParams.set('user', filterInputs.user.trim());
    if (filterInputs.since?.isValid()) nextParams.set('since', filterInputs.since.toISOString());
    if (filterInputs.until?.isValid()) nextParams.set('until', filterInputs.until.toISOString());
    setSearchParams(nextParams);
  };

  const handleClearFilters = (): void => {
    setSearchParams(new URLSearchParams());
  };

  const handleRefresh = (): void => {
    loadAuditEntries(activeFilters, paginationModel.page * paginationModel.pageSize, paginationModel.pageSize);
  };

  const handlePaginationChange = (model: GridPaginationModel): void => {
    setPaginationModel(model);
    if (model.pageSize !== paginationModel.pageSize) {
      return; // pageSize change is handled by the effect via reset to page 0
    }
    loadAuditEntries(activeFilters, model.page * model.pageSize, model.pageSize);
  };

  const columns = useMemo<GridColDef<AuditLogEntry>[]>(() => {
    const textCell = (params: GridRenderCellParams<AuditLogEntry, string | null | undefined>) => {
      const text = params.value ?? '-';
      return (
        <Tooltip title={text} placement="top-start" enterDelay={400}>
          <Typography variant="body2" noWrap sx={{ width: '100%' }}>{text}</Typography>
        </Tooltip>
      );
    };

    return [
      {
        field: 'timestamp',
        headerName: t('auditLog.columns.timestamp'),
        width: 170,
        renderCell: (params: GridRenderCellParams<AuditLogEntry, string | undefined>) => (
          <Typography variant="body2" noWrap>
            {params.value ? dayjs(params.value).format('DD.MM.YYYY HH:mm:ss') : '-'}
          </Typography>
        ),
      },
      {
        field: 'user',
        headerName: t('auditLog.columns.user'),
        flex: 1,
        minWidth: 160,
        valueGetter: (_, row) => row.user ?? '-',
        renderCell: textCell,
      },
      {
        field: 'referenceType',
        headerName: t('auditLog.columns.referenceType'),
        width: 140,
        valueGetter: (_, row) => row.referenceType ?? '-',
        renderCell: textCell,
      },
      {
        field: 'referenceId',
        headerName: t('auditLog.columns.referenceId'),
        flex: 1,
        minWidth: 160,
        valueGetter: (_, row) => row.referenceId ?? '-',
        renderCell: textCell,
      },
      {
        field: 'function',
        headerName: t('auditLog.columns.function'),
        width: 170,
        valueGetter: (_, row) => row.function ?? '-',
        renderCell: textCell,
      },
      {
        field: 'message',
        headerName: t('auditLog.columns.message'),
        flex: 1.5,
        minWidth: 200,
        valueGetter: (_, row) => row.message ?? '-',
        renderCell: textCell,
      },
      {
        field: 'details',
        headerName: t('auditLog.columns.details'),
        flex: 1.5,
        minWidth: 200,
        valueGetter: (_, row) => row.details ?? '-',
        renderCell: textCell,
      },
      {
        field: 'clientIp',
        headerName: t('auditLog.columns.clientIp'),
        width: 140,
        valueGetter: (_, row) => row.clientIp ?? '-',
        renderCell: textCell,
      },
      {
        field: 'userAgent',
        headerName: t('auditLog.columns.userAgent'),
        flex: 1.2,
        minWidth: 200,
        valueGetter: (_, row) => row.userAgent ?? '-',
        renderCell: textCell,
      },
    ];
  }, [t]);

  /**
   * Resolves a stable row identifier for the audit grid.
   * @param {AuditLogEntry} row The current audit row.
   * @returns {string} Stable identifier string.
   */
  const getRowId = useCallback((row: AuditLogEntry): string => {
    return row.id ?? `${row.timestamp ?? 'unknown'}-${row.referenceId ?? 'na'}-${row.function ?? 'na'}`;
  }, []);

  /**
   * Checks whether an audit entry matches currently active filters.
   */
  const matchesActiveFilters = useCallback((entry: AuditLogEntry, filters: FilterState): boolean => {
    if (filters.referenceType && (entry.referenceType ?? '').toLowerCase() !== filters.referenceType.toLowerCase()) return false;
    if (filters.referenceId && (entry.referenceId ?? '').toLowerCase() !== filters.referenceId.toLowerCase()) return false;
    if (filters.user && (entry.user ?? '').toLowerCase() !== filters.user.toLowerCase()) return false;
    if (filters.since?.isValid() && entry.timestamp && dayjs(entry.timestamp).isBefore(filters.since)) return false;
    if (filters.until?.isValid() && entry.timestamp && dayjs(entry.timestamp).isAfter(filters.until)) return false;
    if (filters.contains) {
      const needle = filters.contains.toLowerCase();
      const haystack = [entry.user, entry.referenceType, entry.referenceId, entry.function, entry.message, entry.details]
        .map((v) => (v ?? '').toLowerCase())
        .join(' ');
      if (!haystack.includes(needle)) return false;
    }
    return true;
  }, []);

  /**
   * Upserts a new audit entry at the top of the current page while preventing duplicates.
   * Live updates only appear on the first page.
   */
  const insertRealtimeEntry = useCallback((entry: AuditLogEntry): void => {
    if (paginationModel.page !== 0) return;
    setEntries((prevEntries) => {
      const exists = prevEntries.some((current) => getRowId(current) === getRowId(entry));
      if (exists) return prevEntries;
      const next = [entry, ...prevEntries];
      return next.slice(0, paginationModel.pageSize);
    });
    setTotalCount((prev) => prev + 1);
  }, [getRowId, paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    if (!context.token) {
      setRealtimeConnected(false);
      return;
    }

    const connection = new HubConnectionBuilder()
      .withUrl(`${context.baseUrl}${AUDIT_LOG_HUB_PATH}`, {
        accessTokenFactory: () => context.token ?? '',
        transport: HttpTransportType.WebSockets,
        skipNegotiation: true,
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build();

    const handleIncomingEntry = (entry: AuditLogEntry): void => {
      if (!matchesActiveFilters(entry, activeFilters)) return;
      insertRealtimeEntry(entry);
    };

    AUDIT_LOG_EVENT_NAMES.forEach((eventName) => {
      connection.on(eventName, handleIncomingEntry);
    });

    connection.onclose(() => setRealtimeConnected(false));
    connection.onreconnected(async () => {
      try {
        await connection.invoke(AUDIT_LOG_REGISTER_METHOD);
        setRealtimeConnected(true);
      } catch {
        setRealtimeConnected(false);
      }
    });
    connection.onreconnecting(() => setRealtimeConnected(false));

    let aborted = false;
    const connect = async (): Promise<void> => {
      try {
        await connection.start();
        if (aborted) {
          await connection.stop();
          return;
        }
        await connection.invoke(AUDIT_LOG_REGISTER_METHOD);
        setRealtimeConnected(true);
      } catch (err: any) {
        if (aborted) return;
        console.error('[AdminAuditLog] SignalR connection error:', err);
        setRealtimeConnected(false);
      }
    };
    connect();

    return () => {
      aborted = true;
      const disconnect = async (): Promise<void> => {
        try {
          if (connection.state === HubConnectionState.Connected) {
            await connection.invoke(AUDIT_LOG_UNREGISTER_METHOD);
          }
        } finally {
          await connection.stop();
          setRealtimeConnected(false);
        }
      };
      disconnect();
    };
  }, [context.baseUrl, context.token, activeFilters, matchesActiveFilters, insertRealtimeEntry]);

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h4" component="h1">{t('auditLog.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('auditLog.subtitle')}</Typography>
          <Typography variant="caption" color={realtimeConnected ? 'success.main' : 'text.secondary'}>
            {realtimeConnected ? t('auditLog.live.connected') : t('auditLog.live.disconnected')}
          </Typography>
        </Box>
        <Button variant="outlined" onClick={handleRefresh}>{t('auditLog.actions.refresh')}</Button>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          <TextField
            fullWidth
            label={t('auditLog.filters.contains')}
            placeholder={t('auditLog.filters.containsPlaceholder')}
            value={filterInputs.contains}
            onChange={(event) => setFilterInputs((prev) => ({ ...prev, contains: event.target.value }))}
            onKeyDown={(event) => { if (event.key === 'Enter') handleApplyFilters(); }}
          />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label={t('auditLog.filters.referenceType')}
              value={filterInputs.referenceType}
              onChange={(event) => setFilterInputs((prev) => ({ ...prev, referenceType: event.target.value }))}
              onKeyDown={(event) => { if (event.key === 'Enter') handleApplyFilters(); }}
            />
            <TextField
              fullWidth
              label={t('auditLog.filters.referenceId')}
              value={filterInputs.referenceId}
              onChange={(event) => setFilterInputs((prev) => ({ ...prev, referenceId: event.target.value }))}
              onKeyDown={(event) => { if (event.key === 'Enter') handleApplyFilters(); }}
            />
            <TextField
              fullWidth
              label={t('auditLog.filters.user')}
              value={filterInputs.user}
              onChange={(event) => setFilterInputs((prev) => ({ ...prev, user: event.target.value }))}
              onKeyDown={(event) => { if (event.key === 'Enter') handleApplyFilters(); }}
            />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: { xs: 'stretch', md: 'center' } }}>
            <DateTimePicker
              label={t('auditLog.filters.since')}
              value={filterInputs.since}
              onChange={(value) => setFilterInputs((prev) => ({ ...prev, since: value }))}
              slotProps={{ textField: { fullWidth: true } }}
            />
            <DateTimePicker
              label={t('auditLog.filters.until')}
              value={filterInputs.until}
              onChange={(value) => setFilterInputs((prev) => ({ ...prev, until: value }))}
              slotProps={{ textField: { fullWidth: true } }}
            />
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
              <Button variant="contained" onClick={handleApplyFilters}>{t('auditLog.filters.apply')}</Button>
              <Button variant="text" onClick={handleClearFilters}>{t('auditLog.filters.clear')}</Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ width: '100%', minWidth: 0, height: 'calc(100vh - 360px)', minHeight: 420 }}>
        <DataGrid
          loading={loading}
          rows={entries}
          columns={columns}
          getRowId={getRowId}
          rowHeight={40}
          columnHeaderHeight={44}
          disableRowSelectionOnClick
          paginationMode="server"
          rowCount={totalCount}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationChange}
          pageSizeOptions={[25, 50, 100, 200]}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          localeText={{ noRowsLabel: t('auditLog.noRows') }}
          sx={{
            width: '100%',
            '& .MuiDataGrid-cell': { alignItems: 'center' },
            '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600 },
          }}
        />
      </Box>
    </Box>
  );
};
